import type { RoomUser } from './types'
import { roomRequest } from './roomApi'
import { useCallback, useEffect, useState } from 'react'

export type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated'

export function useAuth() {
  const [status, setStatus] = useState<AuthStatus>('checking')
  const [user, setUser] = useState<RoomUser | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    const check = async () => {
      try {
        const response = await fetch('/api/auth', {
          cache: 'no-store',
          credentials: 'same-origin',
        })
        const value = (await response.json()) as {
          authenticated?: boolean
          user?: RoomUser
        }
        if (!cancelled) {
          setUser(value.user || null)
          setStatus(
            value.authenticated && value.user
              ? 'authenticated'
              : 'unauthenticated',
          )
        }
      } catch {
        if (!cancelled) {
          setStatus('unauthenticated')
          setError('The Bramble server is not reachable yet.')
        }
      }
    }
    void check()
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(
    async (username: string, password: string) => {
      setBusy(true)
      setError('')
      try {
        const response = await fetch('/api/login', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        })
        const value = (await response.json()) as {
          error?: string
          user: RoomUser
        }
        if (!response.ok) {
          setError(value.error || 'Bramble stayed locked.')
          return false
        }
        setUser(value.user)
        setStatus('authenticated')
        return true
      } catch {
        setError('The Bramble server is not reachable yet.')
        return false
      } finally {
        setBusy(false)
      }
    },
    [],
  )

  const logout = useCallback(async () => {
    try {
      await fetch('/api/logout', { method: 'POST', credentials: 'same-origin' })
    } finally {
      setUser(null)
      setStatus('unauthenticated')
      setError('')
    }
  }, [])

  const markUnauthorized = useCallback(() => {
    setStatus('unauthenticated')
    setUser(null)
    setError('Please sign in again to continue with your writing account.')
  }, [])

  const updateProfile = async (body: unknown) => {
    if (!user) throw new Error('Sign in again.')
    const value = await roomRequest<{ user: RoomUser }>(
      user.id,
      '/api/me',
      'PATCH',
      body,
    )
    setUser(value.user)
  }
  return {
    status,
    user,
    busy,
    error,
    login,
    logout,
    markUnauthorized,
    updateProfile,
  }
}
